import { Box, Menu, MenuItem, Skeleton } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useChat } from "../../../context/ChatProvider";
import { MessageProps } from "../../../interfaces/all";
import { isEmojiOnly } from "../../../utils/message";
import EmojiMessage from "./EmojiMessage";
import ImageMessage from "./ImageMessage";
import LinkPreview from "./LinkPreview";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import {
  MessageContainer,
  MessageContent,
  MessageSender,
  MessageText,
  MessageTextLine,
} from "./styles";
// Novos componentes de mensagem
import ButtonMessage from "./ButtonMessage";
import DocumentMessage from "./DocumentMessage";
import ReactionMessage from "./ReactionMessage";
import TemplateMessage from "./TemplateMessage";
import UnsupportedMessage from "./UnsupportedMessage";
import VideoMessage from "./VideoMessage";
import VoiceMessage from "./VoiceMessage";

interface MessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const Message: React.FC<MessagePropsSkeleton> = React.memo(
  ({ message, showAvatar = true, showDate = true, showSkeleton = false }) => {
    const isFromClient = message.sentBy === "client"; // CORRIGIDO: 'client' são mensagens do cliente
    const { selectedConversation } = useChat();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    // Usar apenas o status da mensagem, sem o hook
    const currentReadStatus = message.readStatus || { isRead: false };

    const showTriangle = showAvatar;

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (isFromClient && message._id) {
          setMenuAnchorEl(event.currentTarget);
        }
      },
      [isFromClient, message._id],
    );

    const handleCloseMenu = useCallback(() => {
      setMenuAnchorEl(null);
    }, []);

    const handleMarkAsRead = useCallback(() => {
      if (message._id) {
        // Implement the logic to mark the message as read
      }
      handleCloseMenu();
    }, [message._id, handleCloseMenu]);

    const handleMarkAsUnread = useCallback(() => {
      if (message._id) {
        // Implement the logic to mark the message as unread
      }
      handleCloseMenu();
    }, [message._id, handleCloseMenu]);

    const getSenderName = useCallback(() => {
      const formatName = (name: string) => {
        return name
          .split(" ")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ");
      };

      let name: string;
      switch (message.sentBy) {
        case "client":
          name =
            selectedConversation?.valid_name ||
            selectedConversation?.name ||
            "Cliente";
          return formatName(name);
        case "user":
          name = selectedConversation?.attendant_name || "Atendente";
          return formatName(name);
        default:
          return "Usuário";
      }
    }, [
      message.sentBy,
      selectedConversation?.valid_name,
      selectedConversation?.name,
      selectedConversation?.attendant_name,
    ]);

    const renderContent = useCallback(() => {
      switch (message.type) {
        case "reaction":
          return (
            <ReactionMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "unsupported":
          return (
            <UnsupportedMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "template":
          return (
            <TemplateMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "button":
          return (
            <ButtonMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "image":
          return (
            <ImageMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "image_text":
        case "sticker":
          return (
            <ImageMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "voice":
        case "audio":
          return (
            <VoiceMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "video":
          return (
            <VideoMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        case "document":
          return (
            <DocumentMessage
              message={message}
              showAvatar={showAvatar}
              showDate={showDate}
            />
          );
        default:
          if (message.content.text && isEmojiOnly(message.content.text)) {
            return (
              <EmojiMessage
                message={message}
                showAvatar={showAvatar}
                showDate={showDate}
              />
            );
          }
          return (
            <>
              <MessageTextLine
                style={{
                  position: "relative",
                  paddingRight: 62,
                  minHeight: 24,
                }}
              >
                {message.type === "text" && message.content.text && (
                  <MessageText>{message.content.text}</MessageText>
                )}
                {message.type !== "text" && message.content.text && (
                  <MessageText>{message.content.text}</MessageText>
                )}
              </MessageTextLine>
              {message.content.text && <LinkPreview content={message} />}
            </>
          );
      }
    }, [message, showAvatar, showDate]);

    if (showSkeleton) {
      return (
        <MessageContainer isFromClient={true} showTriangle={showAvatar}>
          {showAvatar && (
            <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          )}
          <MessageContent isFromClient={true} showTriangle={showAvatar}>
            {showAvatar && (
              <MessageSender isFromClient={true}>
                <Skeleton variant="text" width={80} height={18} />
              </MessageSender>
            )}
            <MessageTextLine
              style={{ position: "relative", paddingRight: 62, minHeight: 24 }}
            >
              <Skeleton
                variant="rectangular"
                width={180}
                height={20}
                sx={{ borderRadius: 2 }}
              />
              <Box sx={{ position: "absolute", right: 0, bottom: 2 }}>
                <Skeleton variant="circular" width={16} height={16} />
              </Box>
            </MessageTextLine>
          </MessageContent>
        </MessageContainer>
      );
    }

    // Se for um tipo especial que já tem sua própria estrutura completa, renderizar diretamente
    if (
      [
        "template",
        "reaction",
        "unsupported",
        "button",
        "image",
        "image_text",
        "sticker",
        "voice",
        "audio",
        "video",
        "document",
      ].includes(message.type)
    ) {
      return renderContent();
    }

    // Se for uma mensagem de emoji, também renderizar diretamente (já tem MessageMetaInfo próprio)
    if (message.content.text && isEmojiOnly(message.content.text)) {
      return renderContent();
    }

    return (
      <>
        <MessageContainer
          id={`message-${message._id}`}
          isFromClient={isFromClient}
          showTriangle={showTriangle}
          onContextMenu={handleContextMenu}
        >
          {isFromClient ? (
            <>
              <MessageAvatar
                sentBy={message.sentBy}
                showAvatar={showAvatar}
                data-show={showAvatar}
              />
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{
                    position: "relative",
                    paddingRight: 62,
                    minHeight: 24,
                  }}
                >
                  {renderContent()}
                  <MessageMetaInfo
                    message={message}
                    showDate={showDate}
                    isFromClient={isFromClient}
                    readStatus={currentReadStatus}
                  />
                </MessageTextLine>
              </MessageContent>
            </>
          ) : (
            <>
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{
                    position: "relative",
                    paddingRight: 62,
                    minHeight: 24,
                  }}
                >
                  {renderContent()}
                  <MessageMetaInfo
                    message={message}
                    showDate={showDate}
                    isFromClient={isFromClient}
                    readStatus={currentReadStatus}
                  />
                </MessageTextLine>
              </MessageContent>
              <MessageAvatar
                sentBy={message.sentBy}
                showAvatar={showAvatar}
                data-show={showAvatar}
              />
            </>
          )}
        </MessageContainer>
        {isFromClient && message._id && (
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseMenu}
            onClick={handleCloseMenu}
          >
            {currentReadStatus?.isRead ? (
              <MenuItem onClick={handleMarkAsUnread}>
                Marcar como não lida
              </MenuItem>
            ) : (
              <MenuItem onClick={handleMarkAsRead}>Marcar como lida</MenuItem>
            )}
          </Menu>
        )}
      </>
    );
  },
);

Message.displayName = "Message";

export default Message;
